import { usePagination } from './pagination.ts'
import { LocalizationStore, TranslationKey } from '@/stores/localization.ts'
import type { Component } from 'vue'
import type { IconName } from '@/icons'
import {
  Novelty,
  NoveltyRelationTypeType,
  OrderDirection,
  ReleaseEntry,
  ReleaseEntryTypeType,
  ReleaseItem,
  UserReleaseEntryStatus,
  UserReleaseEntryStatusCollectionStatusType,
  UserReleaseEntryStatusCurrentReadStatusType,
  UserReleaseReadStatusStatusType,
} from '../../__generated__/graphql.ts'
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'
import { LineBuilderConfig, PreviewEntryData } from '@/components/PreviewEntry.vue'
import { type DocumentNode } from '@/vendor/apollo-client'
import { catalogQuery } from '@/data/catalogClient.ts'
import { sendError } from '@/utils/sendError.ts'
import { MiscOwnedMap, UserCollectionStore } from '@/stores/user-collection'
import { match, P } from 'ts-pattern'
import dayjs from 'dayjs'
import { showToast } from '@/utils/showToast'
import { GET_RELEASE_ITEM_COLLECTIONS } from '@/queries'

export const COLLECT_STATUS = Object.values(UserReleaseEntryStatusCollectionStatusType)
export const READ_STATUS = Object.values(UserReleaseReadStatusStatusType)

// Characters that do not decompose under Unicode NFKD and need explicit folding.
// Keep in sync with atsume-cms/utils.ts foldDiacritics.
const DIACRITIC_FOLD_MAP: Record<string, string> = {
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  ø: 'o',
  ł: 'l',
  đ: 'd',
  ð: 'd',
  þ: 'th',
  ı: 'i',
  ŧ: 't',
  ħ: 'h',
  ĸ: 'k',
}

// Collapse diacritics/ligatures to plain ASCII letters so "Café", "Cafe" and
// "Cafè" all search identically. Must stay identical to the cms-side copy.
export function foldDiacritics(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[ßæœøłđðþıŧħĸ]/g, (ch) =>
      DIACRITIC_FOLD_MAP[ch] === undefined ? ch : DIACRITIC_FOLD_MAP[ch],
    )
}

export function normalizeSearchInput(input: string): string[] {
  return foldDiacritics(input.toLowerCase())
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/ +/g, ' ')
    .trim()
    .split(' ')
    .filter((w) => w !== 'and' && w !== 'und')
}

export function getMainName(name: string) {
  return name.split('.')[0]
}

export function publisherNameToCssClass(name: string) {
  return `pub-${name
    .replace(/[^\x00-\x7F]/g, '')
    .replace(/[ &+-.]/g, '')
    .toLowerCase()
    .trim()}`
}

export function areArraysEqual(
  a: (string | number)[] | undefined | null,
  b: (string | number)[] | undefined | null,
) {
  return a && b && a.length === b.length && a.every((value, index) => value === b[index])
}

function isEmptyFilterValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === false ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  )
}

export function didFilterChange(
  currentFilter: ReleaseFilterProps,
  updatedFilter: ReleaseFilterProps,
  propsToIgnore: (keyof ReleaseFilterProps)[] = [],
) {
  const keys = new Set<keyof ReleaseFilterProps>([
    ...(Object.keys(currentFilter) as (keyof ReleaseFilterProps)[]),
    ...(Object.keys(updatedFilter) as (keyof ReleaseFilterProps)[]),
  ])
  for (const key of keys) {
    if (propsToIgnore.includes(key)) {
      continue
    }
    const oldValue = currentFilter[key]
    const newValue = updatedFilter[key]
    if (isEmptyFilterValue(oldValue) && isEmptyFilterValue(newValue)) {
      continue
    }
    if (oldValue instanceof Array && newValue instanceof Array) {
      if (!areArraysEqual(oldValue, newValue)) return true
      continue
    }
    if (oldValue !== newValue) return true
  }
  return false
}

export function resetFilterProps(
  newDefaultFilter: ReleaseFilterProps,
  oldFilter: ReleaseFilterProps,
  propsToRetain: (keyof ReleaseFilterProps)[] = [],
) {
  const retainedProps = Object.fromEntries(propsToRetain.map((key) => [key, oldFilter[key]]))
  return Object.assign(newDefaultFilter, retainedProps)
}

export const didSortChange = (currentSort: ReleaseEntrySorts, updatedSort: ReleaseEntrySorts) => {
  if (currentSort.length !== updatedSort.length) {
    return true
  }
  return currentSort.some((sort, index) => {
    const updated = updatedSort[index]
    return (
      sort.label !== updated.label ||
      sort.property !== updated.property ||
      sort.order !== updated.order
    )
  })
}

export const COLLECTION_STATUS_LABEL_MAP: Record<
  UserReleaseEntryStatusCollectionStatusType,
  TranslationKey
> = {
  planned: 'collectStatusPlanned',
  collecting: 'collectStatusCollecting',
  done: 'collectStatusDone',
  aborted: 'collectStatusAborted',
  sold: 'collectStatusSold',
  paused: 'collectStatusPaused',
}

export const READ_STATUS_LABEL_MAP: Record<
  UserReleaseEntryStatusCurrentReadStatusType,
  TranslationKey
> = {
  notPlanned: 'readStatusNotPlanned',
  planned: 'readStatusPlanned',
  reading: 'readStatusReading',
  done: 'readStatusDone',
  aborted: 'readStatusAborted',
  paused: 'readStatusPaused',
}

export type ReadSelectionMap = { [number: string]: boolean }
export type OwnedSelectionMap = { [number: string]: number }
export const SELECTION_TABLE_ITEMS_PER_PAGE = 10

export type CollectionBulkEditTab = 'collection' | 'volume'

export type BulkEditModalProps = {
  entryId: string
  collectionEntry?: BulkModalCollectionEntry
  defaultSelectedTab?: CollectionBulkEditTab
  preselectedVersions?: string[]
  preselectedReadNumbers?: string[]
  preselectedIcon?: IconName
  header: TranslationKey
  selectedCollectionId?: string
  localOnly?: boolean
  preselectedCollectionStatus?: UserReleaseEntryStatusCollectionStatusType
}

export type BulkModalCollectionEntry = {
  id: string
  ownedMap: { [_key: string]: number }
  miscOwnedMap: MiscOwnedMap
  latestReadMap: ReadSelectionMap
  collectionStatus: UserReleaseEntryStatusCollectionStatusType
  currentReadStatus: UserReleaseEntryStatusCurrentReadStatusType
}

export const START_PAGES = ['', 'lists', 'exploration', 'settings'] as const
export const START_PAGE_LABEL_MAP: Record<StartPage, TranslationKey> = {
  '': 'bottomNavNovelties',
  lists: 'bottomNavMyLists',
  exploration: 'bottomNavExploration',
  settings: 'bottomNavSettings',
}
export type StartPage = (typeof START_PAGES)[number]

export type ReleaseItemsFilterProps = {
  selectedMonth?: number
  selectedYear?: number
  selectedPublisher?: string[]
  excludedPublisher?: string[]
  selectedMedium?: ReleaseEntryTypeType[]
  excludedMedium?: ReleaseEntryTypeType[]
  selectedTags?: string[]
  excludedTags?: string[]
  selectedDemographies?: string[]
  excludedDemographies?: string[]
  seriesTitle?: string
  showNoDuplicates?: boolean
  hidePlanned?: boolean
  hideCollecting?: boolean
  hidePaused?: boolean
  onlyShowFromCollection?: boolean
  excludeFromCollection?: boolean
  releaseEntryIds?: string[]
  showFirstVolumes?: boolean
  selectedPriceSpan?: [number, number]
  startDate?: string | null
  endDate?: string | null
  collectionFilterMode?: 'unowned' | 'unread' | 'allDone' | 'missing' | 'gaps'
  selectedRelationType?: NoveltyRelationTypeType[]
  selectedCuratedLists?: string[]
  excludedCuratedLists?: string[]
}

export type ReleaseEntryFilterProps = {
  selectedPublisher?: string[]
  excludedPublisher?: string[]
  selectedMedium?: ReleaseEntryTypeType[]
  excludedMedium?: ReleaseEntryTypeType[]
  selectedTags?: string[]
  excludedTags?: string[]
  selectedDemographies?: string[]
  excludedDemographies?: string[]
  seriesTitle?: string
  startDate?: string | null
  endDate?: string | null
  excludeNoStartDate?: boolean
  minScoreField?: string
  selectedCuratedLists?: string[]
  excludedCuratedLists?: string[]
}

export type CollectionFilterProps = {
  selectedPublisher?: string[]
  excludedPublisher?: string[]
  selectedMedium?: ReleaseEntryTypeType[]
  excludedMedium?: ReleaseEntryTypeType[]
  selectedTags?: string[]
  excludedTags?: string[]
  selectedDemographies?: string[]
  excludedDemographies?: string[]
  seriesTitle?: string
  startDate?: string | null
  endDate?: string | null
  excludeNoStartDate?: boolean
  statusFilter?: 'collection' | 'read'
  collectionStatus?: UserReleaseEntryStatusCollectionStatusType | null
  readStatus?: UserReleaseEntryStatusCurrentReadStatusType | null
  collectionFilterMode?: 'unowned' | 'unread' | 'allDone' | 'missing' | 'gaps'
}

export type NoveltiesFilterProps = {
  selectedMonth?: number
  selectedYear?: number
  selectedPublisher?: string[]
  excludedPublisher?: string[]
  selectedMedium?: ReleaseEntryTypeType[]
  excludedMedium?: ReleaseEntryTypeType[]
  showNoDuplicates?: boolean
  hidePlanned?: boolean
  hideCollecting?: boolean
  hidePaused?: boolean
  selectedTags?: string[]
  excludedTags?: string[]
  selectedDemographies?: string[]
  excludedDemographies?: string[]
  seriesTitle?: string
  onlyShowFromCollection?: boolean
  excludeFromCollection?: boolean
  showFirstVolumes?: boolean
  selectedPriceSpan?: [number, number]
  startDate?: string | null
  endDate?: string | null
  collectionFilterMode?: 'unowned' | 'unread' | 'allDone' | 'missing' | 'gaps'
  selectedRelationType?: NoveltyRelationTypeType[]
  selectedCuratedLists?: string[]
  excludedCuratedLists?: string[]
}

export type ListMode = 'filtered' | 'curated'

export type TabConfig<T> = {
  title: string
  key: string
  tabData: T
  widthKey?: string
  visibleOnStart?: boolean
  itemMovedCheck?: (item: PreviewEntryData) => boolean
}

export type EndlessScrollConfig<T> = {
  loadMoreStart: () => Promise<TabConfig<T>[]>
  loadMoreEnd: () => Promise<TabConfig<T>[]>
  tabStartOffset: number
  contentStartOffset: number
}

export type PageHeightScrollConfig = {
  currentPageScroll: number
  startTabScrollPosition: number
}

export interface PreviewListProps<Q extends DocumentNode> {
  query: Q
  releaseItemFilter: ReleaseItemsFilterProps
  sortParams: ReleaseEntrySorts
  showScrollOnStart?: boolean
  getWhere: (filter: ReleaseFilterProps) => Partial<VariablesOf<Q>>
  getSort: (sorts: ReleaseEntrySorts) => Partial<VariablesOf<Q>>
  getPaginationData: (result: ResultOf<Q>) => PreviewEntryData[]
  lineBuilder: LineBuilderConfig
  itemMovedCheck?: (item: PreviewEntryData) => boolean
  refresh?: () => Promise<void>
  noItemsComponent?: Component
  maxItemsToLoad?: number
  disableCache?: boolean
  fullPageHeightScroll?: boolean
  hideAddToCollection?: boolean
  disableRefresh?: boolean
  hideTopPadding?: boolean
  noveltyAddMode?: boolean
  reserveFabSpace?: boolean
}

export type ReleaseFilterProps = ReleaseItemsFilterProps &
  ReleaseEntryFilterProps &
  CollectionFilterProps

export type ReleaseEntrySort = {
  label: TranslationKey
  property:
    | keyof ReleaseEntry
    | keyof ReleaseItem
    | keyof UserReleaseEntryStatus
    | keyof Novelty
    | 'orderNumber'
  order: OrderDirection | null
  hidden?: boolean
}

export type ReleaseEntrySorts = ReleaseEntrySort[]

export type ListLabel = TranslationKey | { de: string; en: string }

export function isFreeFormListLabel(label: ListLabel): label is { de: string; en: string } {
  return typeof label !== 'string'
}

export type CollectionListConfig = {
  id: string
  // Stable client-facing route key for custom lists (built-in tabs leave it unset).
  // For an offline-created list it equals the client urlName until the server row
  // arrives; server-op call sites still use `id`.
  urlName?: string
  // True while the list's create is still queued (no server row yet).
  pending?: boolean
  labelKey: ListLabel
  category: 'collection' | 'read'
  mode: ListMode
  statusFilter: 'collection' | 'read'
  collectionStatus: UserReleaseEntryStatusCollectionStatusType | null
  readStatus: UserReleaseEntryStatusCurrentReadStatusType | null
  lineBuilder: LineBuilderConfig
  defaultFilter: CollectionFilterProps
  ignoredFilterPropsForChangeDetection: (keyof ReleaseFilterProps)[]
  defaultSort: ReleaseEntrySorts
  // Built-in tabs (Collecting/Done/…) query the whole collection by status; user
  // custom lists are membership-based and query the curated release-entry endpoint.
  isCustom?: boolean
  showTotalNumber?: boolean
  showTotalValue?: boolean
  maxItems?: number
  hideSearch?: boolean
}

export type DatabaseListType = 'explore' | 'licenses'

export type DatabaseListConfig = {
  id: string
  urlName: string
  labelKey: ListLabel
  subtitle?: ListLabel | null
  category: 'database'
  mode: ListMode
  listType: DatabaseListType
  lineBuilder: LineBuilderConfig
  defaultFilter: ReleaseEntryFilterProps
  ignoredFilterPropsForChangeDetection: (keyof ReleaseFilterProps)[]
  defaultSort: ReleaseEntrySorts
  maxItems?: number
  hideSearch?: boolean
}

export type NoveltyCustomListConfig = {
  id: string
  urlName?: string
  pending?: boolean
  labelKey: ListLabel
  category: 'novelty-custom'
  mode: 'curated'
  lineBuilder: LineBuilderConfig
  defaultFilter: NoveltiesFilterProps
  ignoredFilterPropsForChangeDetection: (keyof NoveltiesFilterProps)[]
  defaultSort: ReleaseEntrySorts
  showTotalNumber?: boolean
  showTotalValue?: boolean
  maxItems?: number
  hideSearch?: boolean
}

export type ListConfig = CollectionListConfig | DatabaseListConfig | NoveltyCustomListConfig

/* eslint-disable @typescript-eslint/no-explicit-any */
function responsive(width: number, xs: any, sm: any, md: any, xl: any): any {
  if (width < 420) {
    return xs
  }

  if (width >= 420 && width < 700) {
    return sm
  }

  if (width >= 700 && width < 1000) {
    return md
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return xl
}

function getVolumeNumberFromVersion(s: string): string {
  const splits = s.split('-')
  return splits[0]
}

export function getFormattedVolumeNumberFromVersionPretty(v: string): string {
  const n = getVolumeNumberFromVersion(v)
  return n.length > 0 && Number(n) % 1 !== 0 ? `${v}` : n.split('.')[0]
}

function getSuffixFromVolumeVersion(s: string) {
  const splits = s.split(/-(.*)/)
  return splits.length > 1 ? splits[1] : ''
}

function getVersionFromVolume(volume: { number?: string; volumeSuffix?: string | null }) {
  return typeof volume.number === 'string' && volume.number.length > 0
    ? `${volume.number}${volume.volumeSuffix ? `-${volume.volumeSuffix}` : ''}`
    : undefined
}

function getSortedUniqueVolumeNumbersFromVolume(volumes: { number?: string }[]) {
  return volumes
    .map((v) => v.number)
    .filter((n): n is string => typeof n === 'string' && n.length > 0)
    .filter((n, index, self) => self.indexOf(n) === index)
    .sort((a, b) => parseFloat(a) - parseFloat(b))
}

async function addVolumesFromCollectionToOwnedMap(
  item: { relationId: string; entryId: string },
  ownedMap: { [key: string]: number },
  t: LocalizationStore['t'],
  updateOwned: UserCollectionStore['updateOwned'],
  updateOwnedMap: UserCollectionStore['updateOwnedMap'],
) {
  let result
  try {
    result = await catalogQuery({
      query: GET_RELEASE_ITEM_COLLECTIONS,
      variables: {
        where: { id: { equals: item.relationId } },
        orderBy: { releaseDate: OrderDirection.Asc },
      },
    })
  } catch (error) {
    // Read failed (offline/network): skip the per-volume expansion but don't abort
    // the caller's toggle — the collection itself is still marked via updateMiscOwned.
    sendError(error, { source: 'addVolumesFromCollectionToOwnedMap' })
    return
  }
  const collectionItems = result.data?.releaseItemCollections || []
  if (collectionItems.length === 1) {
    const ownEntryVolumes = (collectionItems[0]?.releaseItems || []).filter(
      (volume) => volume.entryId === item.entryId,
    )
    ownEntryVolumes.forEach((volume) => {
      const version = getVersionFromVolume(volume)
      updateOwned(item.entryId, version, 1)
    })
    showToastForAddedVolumesToCollection(ownedMap, ownEntryVolumes, t)
    const updatedOwnedMap = ownEntryVolumes
      .map((volume) => getVersionFromVolume(volume))
      .reduce(
        (map, version) => {
          map[version] = 1
          return map
        },
        {} as Record<string, number>,
      )
    updateOwnedMap(item.entryId, updatedOwnedMap)
  }
}

async function showToastForAddedVolumesToCollection(
  ownedMap: { [key: string]: number },
  volumes: { number?: string; volumeSuffix?: string | null }[],
  t: LocalizationStore['t'],
) {
  const itemsNotPartOfOwnedMapYet =
    volumes.filter((volume) => {
      const version = getVersionFromVolume(volume)
      return !ownedMap[version]
    }) || []
  const volumeNumbers = getSortedUniqueVolumeNumbersFromVolume(itemsNotPartOfOwnedMapYet)
  if (volumeNumbers.length > 0) {
    showToast({
      text: t('addedVolumesToCollection', {
        range: formatVolumeRange(volumeNumbers),
        count: volumeNumbers.length,
      }),
    })
  }
}

function formatVolumeNumber(number: string) {
  if (number === '0.0') {
    return ''
  }
  return Number(number) % 1 === 0 ? `${number.split('.')[0]}` : Number(number).toFixed(1)
}

export function formatVolumeNumberFromVersion(version: string) {
  const number = getVolumeNumberFromVersion(version)
  return formatVolumeNumber(number)
}

export function versionHasSuffix(version: string): boolean {
  const splits = version.split(/-(.*)/)
  return splits.length > 1 && splits[1].length > 0
}

export function hasNonZeroPrice(price: string | number | null | undefined): boolean {
  return Number(price) > 0
}

export function formatPrice(price: string | number | null | undefined, language: string): string {
  const value = Number(price)
  if (!Number.isFinite(value)) return ''
  return value.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export type CompatibleAccuracyType = 'day' | 'month' | 'year' | null

export function formatDate(date: string | null, accuracy: 'day' | 'month' | 'year' | null) {
  const format = getDateFormatFromAccuracy(accuracy)
  if (!date || !format) {
    return null
  }
  return dayjs(date).format(format)
}

function getDateFormatFromAccuracy(accuracy: 'day' | 'month' | 'year' | null) {
  return match(accuracy)
    .returnType<string | null>()
    .with('day', () => 'DD.MM.YYYY')
    .with('month', () => 'MM.YYYY')
    .with('year', () => 'YYYY')
    .with(P.nullish, () => null)
    .exhaustive()
}

function formatRange(start: number, end: number): string {
  const startStr = Number.isInteger(start) ? start.toString() : start.toFixed(1)
  const endStr = Number.isInteger(end) ? end.toString() : end.toFixed(1)
  return start === end ? startStr : `${startStr}-${endStr}`
}

function formatVolumeRange(volumeNumbers: string[]): string {
  if (volumeNumbers.length === 0) {
    return '-'
  }

  const numbers = volumeNumbers
    .map((n) => parseFloat(n))
    .filter((n): n is number => !isNaN(n))
    .sort((a, b) => a - b)

  if (numbers.length === 0) {
    return '-'
  }

  const ranges: string[] = []
  let rangeStart = numbers[0]
  let rangeEnd = numbers[0]

  for (let i = 1; i < numbers.length; i++) {
    const current = numbers[i]
    if (current - rangeEnd <= 1.01) {
      rangeEnd = current
    } else {
      ranges.push(formatRange(rangeStart, rangeEnd))
      rangeStart = current
      rangeEnd = current
    }
  }
  ranges.push(formatRange(rangeStart, rangeEnd))

  return ranges.join(', ')
}

function getYear(date: string | null): number | null {
  return date ? parseInt(date.split('-')[0], 10) : null
}

function getMonth(date: string | null): number | null {
  return date ? parseInt(date.split('-')[1], 10) : null
}

export {
  usePagination,
  responsive,
  formatVolumeNumber,
  formatVolumeRange,
  getVolumeNumberFromVersion,
  getSuffixFromVolumeVersion,
  getVersionFromVolume,
  getYear,
  getMonth,
  showToastForAddedVolumesToCollection,
  addVolumesFromCollectionToOwnedMap,
}
